export const IMPORT_DRAFT_PROGRESS_KEY = "kaamfu.import_draft_progress";

// Keep this small — each entry carries a cloned workspace snapshot (meta.draft_state),
// unlike the lightweight id list this pattern was built for.
const MAX_STORED_DRAFTS = 10;

export function isImportDraftId(id) {
    return typeof id === "string" && id.startsWith("import-draft-");
}

/**
 * Server analysis progress id stored inside a persisted import draft snapshot.
 */
export function getImportDraftAnalysisProgressId(draftState) {
    if (!draftState || typeof draftState !== "object") {
        return null;
    }

    const progressId =
        draftState.analysisResults?.progress_id ??
        draftState.analysisProgressId ??
        null;

    if (progressId == null || progressId === "") {
        return null;
    }

    return String(progressId);
}

export function findImportDraftByAnalysisProgressId(
    progresses,
    analysisProgressId,
) {
    if (analysisProgressId == null || analysisProgressId === "") {
        return null;
    }

    const key = String(analysisProgressId);

    return (
        progresses.find(
            (item) =>
                isImportDraftId(item?.id) &&
                getImportDraftAnalysisProgressId(item.meta?.draft_state) === key,
        ) ?? null
    );
}

/**
 * Resolve the operations draft row to update for a queued import snapshot.
 * Prefers analysis progress id; falls back to the draft currently being edited
 * (e.g. pre-analysis draft resumed before its stored snapshot gained an analysis id).
 */
export function resolveImportDraftForUpdate(
    progresses,
    draftState,
    activeImportDraftId = null,
) {
    const analysisProgressId = getImportDraftAnalysisProgressId(draftState);

    const byAnalysisId = analysisProgressId
        ? findImportDraftByAnalysisProgressId(progresses, analysisProgressId)
        : null;

    if (byAnalysisId) {
        return byAnalysisId;
    }

    if (!activeImportDraftId) {
        return null;
    }

    return (
        progresses.find(
            (item) =>
                isImportDraftId(item?.id) && item.id === activeImportDraftId,
        ) ?? null
    );
}

/**
 * Keep one draft row per analysis progress id (latest updated_at wins).
 */
export function dedupeImportDraftProgresses(progresses) {
    const nonDrafts = [];
    const draftsWithoutAnalysisId = [];
    const draftsByAnalysisId = new Map();

    for (const progress of progresses) {
        if (!isImportDraftId(progress?.id)) {
            nonDrafts.push(progress);
            continue;
        }

        const analysisProgressId = getImportDraftAnalysisProgressId(
            progress.meta?.draft_state,
        );

        if (!analysisProgressId) {
            draftsWithoutAnalysisId.push(progress);
            continue;
        }

        const existing = draftsByAnalysisId.get(analysisProgressId);
        if (!existing) {
            draftsByAnalysisId.set(analysisProgressId, progress);
            continue;
        }

        const existingTime = new Date(
            existing.updated_at ?? existing.created_at ?? 0,
        ).getTime();
        const currentTime = new Date(
            progress.updated_at ?? progress.created_at ?? 0,
        ).getTime();

        if (currentTime >= existingTime) {
            draftsByAnalysisId.set(analysisProgressId, progress);
        }
    }

    return [
        ...nonDrafts,
        ...draftsWithoutAnalysisId,
        ...draftsByAnalysisId.values(),
    ];
}

export function loadImportDraftProgress(storage = getSessionStorage()) {
    if (!storage) {
        return [];
    }

    try {
        const raw = storage.getItem(IMPORT_DRAFT_PROGRESS_KEY);

        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter((item) => isImportDraftId(item?.id));
    } catch {
        return [];
    }
}

export function persistImportDraftProgress(
    drafts,
    storage = getSessionStorage(),
) {
    if (!storage) {
        return;
    }

    try {
        storage.setItem(
            IMPORT_DRAFT_PROGRESS_KEY,
            JSON.stringify(drafts.filter((item) => isImportDraftId(item?.id))),
        );
    } catch {
        // Ignore quota / private-mode failures; in-memory drafts still apply.
    }
}

export function upsertImportDraftProgress(
    progress,
    drafts = [],
    storage = getSessionStorage(),
) {
    if (!isImportDraftId(progress?.id)) {
        return [...drafts];
    }

    const analysisProgressId = getImportDraftAnalysisProgressId(
        progress.meta?.draft_state,
    );

    const next = [
        ...drafts.filter((item) => {
            if (item.id === progress.id) {
                return false;
            }

            if (
                analysisProgressId &&
                getImportDraftAnalysisProgressId(item.meta?.draft_state) ===
                    analysisProgressId
            ) {
                return false;
            }

            return true;
        }),
        progress,
    ].slice(-MAX_STORED_DRAFTS);

    persistImportDraftProgress(next, storage);

    return next;
}

export function removeImportDraftProgress(
    id,
    drafts = [],
    storage = getSessionStorage(),
) {
    const next = drafts.filter((item) => item.id !== id);
    persistImportDraftProgress(next, storage);

    return next;
}

function getSessionStorage() {
    try {
        if (typeof sessionStorage === "undefined") {
            return null;
        }

        return sessionStorage;
    } catch {
        return null;
    }
}